import { createContext, useContext, useState } from 'react';
import Option from '../types/Option';
import useApi from '../hooks/useApi';
import { toast } from "react-toastify";
import { FileUploadContext, FileUploadContextProps } from './FileUploadContext';

interface SheetTypeProvider {
  selectedSheetType: Option | null;
  setSelectedSheetType: React.Dispatch<React.SetStateAction<Option | null>>;
}

interface SheetSizeProvider {
  selectedSheetSize: Option | null;
  setSelectedSheetSize: React.Dispatch<React.SetStateAction<Option | null>>;
}

interface ColorProvider {
  selectedColor: Option | null;
  setSelectedColor: React.Dispatch<React.SetStateAction<Option | null>>;
}

interface RingTypeProvider {
  selectedRingType: Option | null;
  setSelectedRingType: React.Dispatch<React.SetStateAction<Option | null>>;
}

interface SideProvider {
  selectedSide: Option | null;
  setSelectedSide: React.Dispatch<React.SetStateAction<Option | null>>;
}

interface CopiesProvider {
  numberOfCopies: number;
  setNumberOfCopies: (value: number) => void;
}

export interface PriceOptions {
  id_papel?: number;
  id_hoja?: number;
  id_anillado?: number;
  id_color?: number;
  id_faz?: number;
  numberOfCopies?: number;
  totalPages?: number;
  postalCode?: string;
}

export interface OrderContextProps {
  sheetType: SheetTypeProvider;
  sheetSize: SheetSizeProvider;
  color: ColorProvider;
  ringType: RingTypeProvider;
  side: SideProvider;
  copies: CopiesProvider;
  shippingPrice: number;
  loadingShipping: boolean;
  totalPrice: number;
  loadingPrice: boolean;
  orderContextClear: any;
  getCalculatedPrice: any;
  getCalculatedShippingCost: any;
}

export const OrderContext = createContext(null);

export const OrderContextProvider = ({ children }: any) => {
  const { calculatePrice, calculateShipping } = useApi();

  const [shippingPrice, setShippingPrice] = useState<any>(null);
  const [totalPrice, setTotalPrice] = useState<any>(null);
  const [loadingShipping, setLoadingShipping] = useState<any>(false);
  const [loadingPrice, setLoadingPrice] = useState<any>(false);

  const [selectedPriceOptions, setSelectedPriceOptions] = useState<any>({
    numberOfCopies: 1,
  });

  const [selectedSheetType, setSelectedSheetType] = useState<Option | null>(
    null
  );
  const sheetType = {
    selectedSheetType,
    setSelectedSheetType,
  };

  const [selectedSheetSize, setSelectedSheetSize] = useState<Option | null>(
    null
  );
  const sheetSize = {
    selectedSheetSize,
    setSelectedSheetSize,
  };

  const [selectedColor, setSelectedColor] = useState<Option | null>(null);
  const color = {
    selectedColor,
    setSelectedColor,
  };

  const [selectedRingType, setSelectedRingType] = useState<Option | null>(null);
  const ringType = {
    selectedRingType,
    setSelectedRingType,
  };

  const [selectedSide, setSelectedSide] = useState<Option | null>(null);
  const side = {
    selectedSide,
    setSelectedSide,
  };

  const [numberOfCopies, setNumberOfCopiesState] = useState<number>(1);
  const setNumberOfCopies = (value: number) => {
    if (value >= 0) {
      setNumberOfCopiesState(value);
    }
  };
  const copies = {
    numberOfCopies,
    setNumberOfCopies,
  };

  const orderContextClear = () => {
    setSelectedSheetType(null);
    setSelectedSheetSize(null);
    setSelectedColor(null);
    setSelectedRingType(null);
    setSelectedSide(null);
    setNumberOfCopies(1);
    setSelectedPriceOptions({ numberOfCopies: 1 });
  };

  const getCalculatedPrice = async (priceOptions: PriceOptions) => {
    setLoadingPrice(true);

    const priceOptionsToCalculate = {
      ...selectedPriceOptions,
      ...priceOptions,
    };

    setSelectedPriceOptions(priceOptionsToCalculate);

    const isValid =
      !!priceOptionsToCalculate?.id_papel &&
      !!priceOptionsToCalculate?.id_hoja &&
      !!priceOptionsToCalculate?.id_anillado &&
      !!priceOptionsToCalculate?.id_color &&
      !!priceOptionsToCalculate?.id_faz &&
      !!priceOptionsToCalculate?.numberOfCopies &&
      !!priceOptionsToCalculate?.totalPages;

    if (isValid) {
      const result = await calculatePrice(priceOptionsToCalculate);

      if (result?.totalPrice) {
        setTotalPrice(result?.totalPrice ?? 0);
      } else {
        setTotalPrice(null);
      }
    } else {
      setTotalPrice(null);
    }

    setLoadingPrice(false);
  };

  const getCalculatedShippingCost = async (priceOptions: PriceOptions) => {
    setLoadingShipping(true)

    const priceOptionsToCalculate = {
      ...selectedPriceOptions,
      ...priceOptions,
    };
    setSelectedPriceOptions(priceOptionsToCalculate);

    const isValid =
      !!priceOptionsToCalculate?.id_papel &&
      !!priceOptionsToCalculate?.id_hoja &&
      !!priceOptionsToCalculate?.id_faz &&
      !!priceOptionsToCalculate?.numberOfCopies &&
      !!priceOptionsToCalculate?.totalPages && 
      !!priceOptionsToCalculate?.postalCode

    if (isValid) {
      const result = await calculateShipping(priceOptionsToCalculate);

      if (result?.shippingPrice) {
        setShippingPrice(result?.shippingPrice ?? 0);
      } else {
        setShippingPrice(null);
        toast.error('Ingrese un código postal valido');
      }
    } else {
      setShippingPrice(null);
    }

    setLoadingShipping(false)
  };

  // Global State like "Redux"
  const contextProps: OrderContextProps = {
    sheetType,
    sheetSize,
    color,
    ringType,
    side,
    copies,
    shippingPrice,
    loadingShipping,
    totalPrice,
    loadingPrice,
    orderContextClear,
    getCalculatedShippingCost,
    getCalculatedPrice,
  };

  return (
    // @ts-ignore
    <OrderContext.Provider value={contextProps}>
      {children}
    </OrderContext.Provider>
  );
};
